import React, { useState, useEffect } from "react";

const BASE = "https://squishyshopapis.squishyshoppies.shop";

// Helper to extract email and password directly from window URL query string
function getUrlAuth() {
    try {
        const params = new URLSearchParams(window.location.search);
        return {
            user_email: params.get("user_email") || "",
            user_password: params.get("user_password") || ""
        };
    } catch (e) {
        return { user_email: "", user_password: "" };
    }
}

function withAuthQuery(url) {
    const { user_email, user_password } = getUrlAuth();
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}user_email=${encodeURIComponent(user_email)}&user_password=${encodeURIComponent(user_password)}`;
}

async function call(url, method = "GET", body = null) {
    try {
        const auth = getUrlAuth();
        const finalUrl = method === "GET" ? withAuthQuery(url) : url;
        const finalBody = method === "GET" ? null : JSON.stringify({ ...(body || {}), ...auth });
        const res = await fetch(finalUrl, {
            method,
            headers: { "Content-Type": "application/json" },
            body: finalBody
        });
        const data = await res.json().catch(() => ({}));
        return { status: res.status, data };
    } catch (e) {
        return { status: 500, data: { detail: "Network or server error" } };
    }
}

// API Calls
const seedAdmin = () => call(`${BASE}/users/seed`, "POST", {});
const getBillings = (page, search = "") => call(`${BASE}/billings?page=${page}&search=${search}`);
const getItems = (page, search = "") => call(`${BASE}/items?page=${page}&search=${search}`);
const getStocks = (item_number, page, search = "") => call(`${BASE}/stocks/${item_number}?page=${page}&search=${search}`);
const createItem = (data) => call(`${BASE}/items`, "POST", data);
const updateItem = (num, data) => call(`${BASE}/items/${num}`, "PUT", data);
const deleteItem = (num) => call(`${BASE}/items/${num}`, "DELETE", {});
const createStock = (data) => call(`${BASE}/stocks`, "POST", data);
const updateStock = (num, data) => call(`${BASE}/stocks/${num}`, "PUT", data);
const deleteStock = (num) => call(`${BASE}/stocks/${num}`, "DELETE", {});
const createBillingWithSellings = (data) => call(`${BASE}/billings/with-sellings`, "POST", data);
const deleteBilling = (num) => call(`${BASE}/billings/${num}`, "DELETE", {});
const updateBilling = (num, data) => call(`${BASE}/billings/${num}`, "PUT", data);
const getSellingsByBilling = (num, page) => call(`${BASE}/sellings/billing/${num}?page=${page}`);
const deleteSelling = (num) => call(`${BASE}/sellings/${num}`, "DELETE", {});
const scanImmudb = (seek = "") => call(`${BASE}/immudb/scan?seek=${encodeURIComponent(seek)}`);
const getRequestLogs = (page) => call(`${BASE}/request-logs?page=${page}`);
const deleteRequestLogs = () => call(`${BASE}/request-logs`, "DELETE", {});
const getGSTReport = (data) => call(`${BASE}/report/gst`, "POST", data);

function uuidWithTime() {
    return (crypto.randomUUID() + "_" + new Date().toISOString()).toLowerCase();
}

function downloadSingleCSV(dataArray, filename) {
    if (!dataArray || dataArray.length === 0) return;
    let csv = Object.keys(dataArray[0]).join(",") + "\n";
    dataArray.forEach(row => {
        csv += Object.values(row).map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",") + "\n";
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename + ".csv";
    a.click();
    URL.revokeObjectURL(url);
}

const styles = `
.AppWrapper{background-image: linear-gradient(43deg, #4158D0 0%, #C850C0 46%, #FFCC70 100%);padding: 3vh 2%;min-height: 94vh;display: flex;flex-direction: column;gap: 15px;font-family: "Inter", sans-serif;}
.TabHeader{display: flex; gap: 10px; flex-wrap: wrap;}
.TabHeader button{padding: 10px 20px; border: none; border-radius: 10px; background: white; font-weight: 600; cursor: pointer; box-shadow: 3px 5px 6px 0 #00038;}
.TabHeader button.active{background: #4158D0; color: white;}
.PageCard{background: #f4f4f6; border-radius: 20px; padding: 20px; box-shadow: 3px 5px 6px 0 #00038; display: flex; flex-direction: column; gap: 12px;}
.HeaderRow{display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px;}
.Btn{padding: 8px 16px; border: none; border-radius: 10px; box-shadow: 3px 5px 6px 0 #00038; cursor: pointer; background: white; font-weight: 500; font-size: 12px; margin: 2px;}
.Btn.Primary{background: #4158D0; color: white;}
.Btn.Danger{background: red; color: white;}
.Input{padding: 10px 15px; border-radius: 10px; border: 1px solid #ccc; width: 100%; max-width: 400px; box-shadow: 3px 5px 6px 0 #00038; font-family: "Inter", sans-serif; margin-top: 8px;}
.TableCard{background: white; border-radius: 15px; padding: 15px; overflow: hidden; margin-top: 10px;}
.TableScroll{overflow-x: auto;}
table{width: 100%; border-collapse: collapse; font-size: 12px; min-width: 500px;}
th, td{padding: 6px; border-bottom: 1px solid #ddd; text-align: left;}
.Pagination{display: flex; gap: 10px; align-items: center; margin-top: 10px;}
.CrossBtn{float: right; border: none; background: red; color: white; border-radius: 50%; width: 25px; height: 25px; cursor: pointer;}
.Total{font-weight: 700; font-size: 18px; color: #333;}
.Error{color: red; font-weight: 600;}.Loading{color: #333; font-weight: 600;}
.SearchBar{display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap;}
.ComponentBlock{border: 2px dashed #4158D0; border-radius: 15px; padding: 15px; background: #fff; display: flex; flex-direction: column; gap: 10px; margin-top: 10px;}
.SubTabs{display: flex; gap: 10px; flex-wrap: wrap;}
.SubTabs button{padding: 8px 16px; border: none; border-radius: 10px; background: white; font-weight: 600; cursor: pointer;}
.SubTabs button.active{background: #4158D0; color: white;}
@media only screen and (max-width: 800px) {
.AppWrapper{min-width: 96%; max-width: 96%; margin-left: 0; padding: 2vh 2%;}
table{font-size: 11px;}
.Btn{padding: 6px 10px; font-size: 11px;}
}
`;

export default function App() {
    const [tab, setTab] = useState("billing");

    useEffect(() => {
        seedAdmin();
    }, []);

    return (
        <div className="AppWrapper">
            <style>{styles}</style>
            <div className="TabHeader">
                <button className={tab === "billing" ? "active" : ""} onClick={() => setTab("billing")}>Billing</button>
                <button className={tab === "stock" ? "active" : ""} onClick={() => setTab("stock")}>Stock Mgmt</button>
                <button className={tab === "report" ? "active" : ""} onClick={() => setTab("report")}>Reports</button>
            </div>
            {tab === "billing" && <BillingSection />}
            {tab === "stock" && <StockSection />}
            {tab === "report" && <ReportSection />}
        </div>
    );
}

// ===== BILLING SECTION =====
function BillingSection() {
    const [page, setPage] = useState("list");
    const [billings, setBillings] = useState([]);
    const [billingPage, setBillingPage] = useState(1);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState("");
    const [error, setError] = useState("");

    const [items, setItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");
    const [itemPage, setItemPage] = useState(1);
    const [selectedItemObj, setSelectedItemObj] = useState(null);
    const [stocks, setStocks] = useState([]);
    const [stockPage, setStockPage] = useState(1);
    const [selectedStocks, setSelectedStocks] = useState([]);
    const [customerName, setCustomerName] = useState("");
    const [customerMobile, setCustomerMobile] = useState("");
    const [customerGst, setCustomerGst] = useState("");

    const [activeBilling, setActiveBilling] = useState(null);
    const [activeSellings, setActiveSellings] = useState([]);
    const [updateData, setUpdateData] = useState({});

    useEffect(() => {
        loadBillings();
        loadItems();
    }, [billingPage]);

    async function loadBillings() {
        setLoading("Loading billings...");
        const res = await getBillings(billingPage, search);
        if (res.status === 200) setBillings(res.data);
        else setError(res.data?.detail || "Could not load billings");
        setLoading("");
    }

    async function loadItems() {
        const res = await getItems(itemPage, itemSearch);
        if (res.status === 200) setItems(res.data);
    }

    async function onSelectItem(e) {
        const itemNumber = e.target.value;
        const itemObj = items.find(i => String(i.item_number) === String(itemNumber)) || null;
        setSelectedItemObj(itemObj);
        setStocks([]);
        setStockPage(1);
        if (itemNumber) {
            const res = await getStocks(itemNumber, stockPage);
            if (res.status === 200) setStocks(res.data);
        }
    }

    function addStock(stock) {
        if (selectedStocks.find(s => s.stock_number === stock.stock_number)) return;

        const totalBrought = Number(stock.total_brought_units_or_quantity) || 1;
        const defaultSellingPrice = Number(stock.selling_price_including_taxes) || 0;
        const initialQty = 1;

        // Dynamic formula calculation
        const calculatedAmount = Math.round((defaultSellingPrice * initialQty) / totalBrought);

        const newStock = {
            uuidstring: uuidWithTime(),
            stock_number: stock.stock_number,
            item_name: selectedItemObj?.item_name || "N/A",
            unit_name: selectedItemObj?.minimum_selling_quantity_unit_name || "N/A",
            category_name: selectedItemObj?.category_name || "N/A",
            quantity: initialQty,
            total_brought_units_or_quantity: totalBrought,
            selling_price_including_taxes: defaultSellingPrice,
            final_manual_selling_price: calculatedAmount
        };
        setSelectedStocks([...selectedStocks, newStock]);
    }

    function updateQty(stock_number, qtyVal) {
        const q = Math.max(0, parseInt(qtyVal) || 0);
        setSelectedStocks(selectedStocks.map(s => {
            if (s.stock_number === stock_number) {
                const autoCalculatedAmount = Math.round((s.selling_price_including_taxes * q) / s.total_brought_units_or_quantity);
                return { ...s, quantity: q, final_manual_selling_price: autoCalculatedAmount };
            }
            return s;
        }));
    }

    function updateManualPrice(stock_number, priceVal) {
        const manualPrice = Math.max(0, parseInt(priceVal) || 0);
        setSelectedStocks(selectedStocks.map(s => s.stock_number === stock_number ? { ...s, final_manual_selling_price: manualPrice } : s));
    }

    function removeSelected(stock_number) {
        setSelectedStocks(selectedStocks.filter(s => s.stock_number !== stock_number));
    }

    const totalAmount = selectedStocks.reduce((acc, s) => acc + (Number(s.final_manual_selling_price) || 0), 0);

    async function publishBill() {
        if (selectedStocks.length === 0) return setError("Add at least 1 stock");
        if (!customerName || !customerMobile) return setError("Customer name and mobile required");
        setLoading("Publishing bill...");
        const payload = {
            uuidstring: uuidWithTime(),
            billing_number: Date.now(),
            customer_name: customerName,
            customer_mobile: customerMobile,
            billing_due_amount: totalAmount,
            customer_gst_number: customerGst,
            billing_date: new Date().toISOString(),
            sellings: selectedStocks.map(s => ({
                uuidstring: uuidWithTime(),
                stock_number: s.stock_number,
                quantity: s.quantity,
                final_manual_selling_price: s.final_manual_selling_price
            }))
        };
        const res = await createBillingWithSellings(payload);
        setLoading("");
        if (res.status === 201) {
            setPage("list");
            setSelectedStocks([]);
            setCustomerName("");
            setCustomerMobile("");
            setCustomerGst("");
            setSelectedItemObj(null);
            setStocks([]);
            loadBillings();
        } else {
            setError(res.data?.detail || "Error publishing bill");
        }
    }

    async function showSellings(billing) {
        setActiveBilling(null);
        const res = await getSellingsByBilling(billing.billing_number, 1);
        if (res.status === 200) {
            setActiveSellings(res.data);
            setActiveBilling({ type: "show", data: billing });
        }
    }

    async function openUpdate(billing) {
        setActiveSellings([]);
        setActiveBilling({ type: "update", data: billing });
        setUpdateData({ ...billing });
    }

    async function confirmUpdate() {
        setLoading("Updating...");
        const res = await updateBilling(updateData.billing_number, { ...updateData, sellings: [] });
        setLoading("");
        if (res.status === 200) {
            setActiveBilling(null);
            loadBillings();
        } else {
            setError(res.data?.detail || "Error updating bill");
        }
    }

    // Exclude buying_price_including_taxes during billing stock selection
    const filteredStocksForBilling = stocks.map(({ buying_price_including_taxes, ...rest }) => rest);

    if (page === "create") {
        return (
            <div className="PageCard">
                <button className="Btn" onClick={() => setPage("list")}>Back</button>
                <h2>Create Bill</h2>
                <p className="Error">{error}</p>
                <p className="Loading">{loading}</p>
                <div className="SearchBar">
                    <input className="Input" placeholder="Search items..." value={itemSearch} onChange={e => setItemSearch(e.target.value)} />
                    <button className="Btn" onClick={() => { setItemPage(1); loadItems(); }}>Search</button>
                </div>
                <select value={selectedItemObj?.item_number || ""} onChange={onSelectItem} className="Input">
                    <option value="">Select Item</option>
                    {items.map(i => <option key={i.item_number} value={i.item_number}>{i.item_name}</option>)}
                </select>
                <div className="Pagination">
                    <button className="Btn" disabled={itemPage === 1} onClick={() => { setItemPage(itemPage - 1); loadItems(); }}>Prev</button>
                    <button className="Btn" onClick={() => { setItemPage(itemPage + 1); loadItems(); }}>Next</button>
                </div>
                {filteredStocksForBilling.length > 0 &&
                    <div className="TableCard">
                        <button className="CrossBtn" onClick={() => setStocks([])}>x</button>
                        <h3>Available Stocks</h3>
                        <Table data={filteredStocksForBilling} actions={["Select"]} onAction={(t, row) => addStock(row)} />
                    </div>
                }
                {selectedStocks.length > 0 &&
                    <div className="TableCard">
                        <h3>Selected Items</h3>
                        <div className="TableScroll">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Item Name</th>
                                        <th>Unit Name</th>
                                        <th>Category Name</th>
                                        <th>Stock No</th>
                                        <th>Qty</th>
                                        <th>Amount</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedStocks.map(s =>
                                        <tr key={s.uuidstring}>
                                            <td>{s.item_name}</td>
                                            <td>{s.unit_name}</td>
                                            <td>{s.category_name}</td>
                                            <td>{s.stock_number}</td>
                                            <td>
                                                <input type="number" min="0" value={s.quantity} onChange={e => updateQty(s.stock_number, e.target.value)} style={{ width: "60px" }} />
                                            </td>
                                            <td>
                                                <input type="number" min="0" value={s.final_manual_selling_price} onChange={e => updateManualPrice(s.stock_number, e.target.value)} style={{ width: "90px" }} />
                                            </td>
                                            <td>
                                                <button className="Btn Danger" onClick={() => removeSelected(s.stock_number)}>Delete</button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
                <p className="Total">Total Amount: ₹{totalAmount}</p>
                <input className="Input" placeholder="Customer Name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
                <input className="Input" placeholder="Customer Mobile" value={customerMobile} onChange={e => setCustomerMobile(e.target.value)} />
                <input className="Input" placeholder="Customer GST" value={customerGst} onChange={e => setCustomerGst(e.target.value)} />
                <button className="Btn Primary" onClick={publishBill}>Publish Bill</button>
            </div>
        );
    }

    return (
        <div className="PageCard">
            <div className="HeaderRow">
                <h2>Billing Management</h2>
                <button className="Btn Primary" onClick={() => setPage("create")}>Create Bill</button>
            </div>
            <div className="SearchBar">
                <input className="Input" placeholder="Search billings..." value={search} onChange={e => setSearch(e.target.value)} />
                <button className="Btn" onClick={loadBillings}>Search</button>
            </div>
            <p className="Error">{error}</p>
            <p className="Loading">{loading}</p>
            <Table data={billings} actions={["Show", "Update", "Delete"]} onAction={(type, row) => { if (type === "Show") showSellings(row); if (type === "Update") openUpdate(row); if (type === "Delete") deleteBilling(row.billing_number).then(loadBillings); }} />
            <div className="Pagination">
                <button className="Btn" disabled={billingPage === 1} onClick={() => setBillingPage(billingPage - 1)}>Prev</button>
                <button className="Btn" onClick={() => setBillingPage(billingPage + 1)}>Next</button>
            </div>
            {activeBilling && activeBilling.type === "show" &&
                <div className="TableCard">
                    <button className="CrossBtn" onClick={() => setActiveBilling(null)}>x</button>
                    <h3>Sellings for Bill #{activeBilling.data.billing_number}</h3>
                    <Table data={activeSellings} actions={["Delete"]} onAction={(type, row) => deleteSelling(row.selling_number).then(() => showSellings(activeBilling.data))} />
                </div>
            }
            {activeBilling && activeBilling.type === "update" &&
                <div className="TableCard">
                    <button className="CrossBtn" onClick={() => setActiveBilling(null)}>x</button>
                    <h3>Update Billing</h3>
                    <input className="Input" value={updateData.customer_name || ""} onChange={e => setUpdateData({ ...updateData, customer_name: e.target.value })} />
                    <input className="Input" value={updateData.customer_mobile || ""} onChange={e => setUpdateData({ ...updateData, customer_mobile: e.target.value })} />
                    <input className="Input" value={updateData.customer_gst_number || ""} onChange={e => setUpdateData({ ...updateData, customer_gst_number: e.target.value })} />
                    <button className="Btn Primary" onClick={confirmUpdate}>Confirm Update</button>
                </div>
            }
        </div>
    );
}

const ITEM_FIELDS = [
    { key: "item_name", label: "Item Name" },
    { key: "minimum_selling_quantity_unit_name", label: "Minimum Selling Quantity Unit Name" },
    { key: "category_name", label: "Category Name" },
];

const STOCK_FIELDS = [
    { key: "total_brought_units_or_quantity", label: "Total Quantity Brought", type: "number", createOnly: true },
    { key: "buying_price_including_taxes", label: "Buying Price (incl. taxes)", type: "number" },
    { key: "selling_price_including_taxes", label: "Selling Price (incl. taxes)", type: "number" },
    { key: "invoice_number", label: "Invoice Number" },
    { key: "seller_name", label: "Seller Name" },
    { key: "seller_gst_number", label: "Seller GST Number" },
    { key: "seller_phone_number", label: "Seller Phone Number" },
    { key: "customer_care_number", label: "Customer Care Number" },
    { key: "manufacturing_date", label: "Manufacturing Date", type: "datetime-local" },
    { key: "expiry_date", label: "Expiry Date", type: "datetime-local" },
];

// ===== STOCK MANAGEMENT SECTION =====
function StockSection() {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [component, setComponent] = useState(null);
    const [loading, setLoading] = useState("");

    useEffect(() => { loadItems(); }, [page]);

    async function loadItems() {
        setLoading("Loading...");
        const res = await getItems(page, search);
        if (res.status === 200) setItems(res.data);
        setLoading("");
    }

    function openComponent(type, data = null) {
        if (component) return alert("Please close existing component first");
        setComponent({ type, data });
    }

    return (
        <div className="PageCard">
            <div className="HeaderRow">
                <h2>Items & Stocks</h2>
                <button className="Btn Primary" onClick={() => openComponent("createItem")}>Create Item</button>
            </div>
            <div className="SearchBar">
                <input className="Input" placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} />
                <button className="Btn" onClick={loadItems}>Search</button>
            </div>
            <p className="Loading">{loading}</p>
            <Table data={items} actions={["Update", "Delete", "Stocks", "Add Stock"]} onAction={(t, row) => {
                if (t === "Update") openComponent("updateItem", row);
                if (t === "Delete") deleteItem(row.item_number).then(res => { if (res.status === 200) loadItems(); else alert(res.data?.detail || "Could not delete item"); });
                if (t === "Stocks") openComponent("showStocks", row);
                if (t === "Add Stock") openComponent("createStock", row);
            }} />
            <div className="Pagination">
                <button className="Btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                <button className="Btn" onClick={() => setPage(page + 1)}>Next</button>
            </div>
            {component && <ComponentRenderer component={component} close={() => setComponent(null)} reload={loadItems} onSwitch={(type, data) => setComponent({ type, data })} />}
        </div>
    );
}

function ComponentRenderer({ component, close, reload, onSwitch }) {
    const isItemForm = component.type === "createItem" || component.type === "updateItem";
    const fields = isItemForm
        ? ITEM_FIELDS
        : (component.type === "createStock" ? STOCK_FIELDS : STOCK_FIELDS.filter(f => !f.createOnly));

    // Pre-fill form state with existing row data
    function buildInitialForm() {
        const initial = {};
        fields.forEach(f => {
            let val = component.data && component.data[f.key] != null ? component.data[f.key] : "";
            if (f.type === "datetime-local" && val) {
                val = new Date(val).toISOString().slice(0, 16);
            }
            initial[f.key] = val;
        });
        return initial;
    }

    const [form, setForm] = useState(buildInitialForm());

    // =========================================================================
    // FIX ADDED HERE: Re-initialize the form state whenever the component or data prop changes (e.g., switching from showStocks to updateStock)
    // =========================================================================
    useEffect(() => {
        setForm(buildInitialForm());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [component]);
    // =========================================================================

    const [stocks, setStocks] = useState([]);
    const [stockPage, setStockPage] = useState(1);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (component.type === "showStocks") loadStocks(); }, [stockPage]);

    async function loadStocks() {
        const res = await getStocks(component.data.item_number, stockPage);
        if (res.status === 200) setStocks(res.data);
    }

    function buildPayload() {
        const payload = {};
        fields.forEach(f => {
            if (f.type === "number") {
                payload[f.key] = Number(form[f.key] || 0);
            } else if (f.type === "datetime-local") {
                payload[f.key] = form[f.key] ? new Date(form[f.key]).toISOString() : new Date().toISOString();
            } else {
                payload[f.key] = form[f.key] || "";
            }
        });
        return payload;
    }

    async function handleSave() {
        setError("");
        setSaving(true);
        const payload = buildPayload();
        let res;
        if (component.type === "createItem") res = await createItem({ ...payload, uuidstring: uuidWithTime() });
        if (component.type === "updateItem") res = await updateItem(component.data.item_number, payload);
        if (component.type === "createStock") res = await createStock({ ...payload, item_number: component.data.item_number, uuidstring: uuidWithTime() });
        if (component.type === "updateStock") res = await updateStock(component.data.stock_number, payload);
        setSaving(false);
        if (res && (res.status === 200 || res.status === 201)) {
            close();
            reload();
        } else {
            setError(res?.data?.detail || "Could not save - please check form fields");
        }
    }

    if (component.type === "showStocks") {
        return (
            <div className="ComponentBlock">
                <button className="CrossBtn" onClick={close}>x</button>
                <h3>Stocks for {component.data.item_name}</h3>
                <Table data={stocks} actions={["Update", "Delete"]} onAction={(t, row) => {
                    if (t === "Update") onSwitch("updateStock", row);
                    if (t === "Delete") deleteStock(row.stock_number).then(res => { if (res.status === 200) loadStocks(); else alert(res.data?.detail || "Could not delete stock"); });
                }} />
                <div className="Pagination">
                    <button className="Btn" disabled={stockPage === 1} onClick={() => setStockPage(stockPage - 1)}>Prev</button>
                    <button className="Btn" onClick={() => setStockPage(stockPage + 1)}>Next</button>
                </div>
            </div>
        );
    }

    const titles = { createItem: "Create Item", updateItem: "Update Item", createStock: "Add Stock", updateStock: "Update Stock" };
    return (
        <div className="ComponentBlock">
            <button className="CrossBtn" onClick={close}>x</button>
            <h3>{titles[component.type]}</h3>
            <p className="Error">{error}</p>
            {fields.map(f =>
                <div key={f.key}>
                    <label style={{ fontSize: "11px", fontWeight: 600, display: "block", marginTop: "6px" }}>{f.label}</label>
                    <input className="Input" type={f.type || "text"} value={form[f.key] ?? ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                </div>
            )}
            <button className="Btn Primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
        </div>
    );
}

// ===== REPORT SECTION =====
function ReportSection() {
    const [logs, setLogs] = useState([]);
    const [type, setType] = useState("request");
    const [showReportComp, setShowReportComp] = useState(false);
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");

    // Request logs pagination
    const [reqPage, setReqPage] = useState(1);

    // Update logs cursor pagination
    const [updateSeekHistory, setUpdateSeekHistory] = useState([""]);
    const [updatePageIndex, setUpdatePageIndex] = useState(0);
    const [nextSeek, setNextSeek] = useState(null);

    useEffect(() => { loadLogs(); }, [type, reqPage, updatePageIndex]);

    async function loadLogs() {
        if (type === "request") {
            const res = await getRequestLogs(reqPage);
            if (res.status === 200) setLogs(res.data);
        } else {
            const currentSeek = updateSeekHistory[updatePageIndex] || "";
            const res = await scanImmudb(currentSeek);
            if (res.status === 200 && res.data) {
                setLogs(res.data.entries || []);
                setNextSeek(res.data.next_seek);

                // Pre-fill next seek in history if it exists
                if (res.data.next_seek) {
                    const newHistory = [...updateSeekHistory];
                    newHistory[updatePageIndex + 1] = res.data.next_seek;
                    setUpdateSeekHistory(newHistory);
                }
            }
        }
    }

    async function generate() {
        if (!start || !end) return alert("Select start and end dates");
        const res = await getGSTReport({ start_datetime: new Date(start).toISOString(), end_datetime: new Date(end).toISOString() });
        if (res.status === 200 && res.data) {
            // Generate 3 separate downloads based on 3 API root keys
            if (res.data.stocks && res.data.stocks.length > 0) {
                downloadSingleCSV(res.data.stocks, "GST_Report_Stocks");
            }
            if (res.data.sellings && res.data.sellings.length > 0) {
                downloadSingleCSV(res.data.sellings, "GST_Report_Sellings");
            }
            if (res.data.summary) {
                downloadSingleCSV([res.data.summary], "GST_Report_Summary");
            }
            setShowReportComp(false);
        } else {
            alert(res.data?.detail || "Failed to generate report");
        }
    }

    return (
        <div className="PageCard">
            <div className="HeaderRow">
                <h2>Reports & Logs</h2>
                <button className="Btn Primary" onClick={() => showReportComp ? alert("Component already open") : setShowReportComp(true)}>Generate Report</button>
            </div>
            {showReportComp &&
                <div className="ComponentBlock">
                    <button className="CrossBtn" onClick={() => setShowReportComp(false)}>x</button>
                    <h3>Generate GST Report</h3>
                    <input type="datetime-local" className="Input" value={start} onChange={e => setStart(e.target.value)} />
                    <input type="datetime-local" className="Input" value={end} onChange={e => setEnd(e.target.value)} />
                    <button className="Btn Primary" onClick={generate}>Generate 3 Excel Downloads</button>
                </div>
            }
            <div className="SubTabs">
                <button className={type === "request" ? "active" : ""} onClick={() => setType("request")}>Request Logs</button>
                <button className={type === "update" ? "active" : ""} onClick={() => setType("update")}>Update Logs</button>
            </div>
            <Table data={logs} />
            <div className="Pagination">
                {type === "request" ? (
                    <>
                        <button className="Btn" disabled={reqPage === 1} onClick={() => setReqPage(reqPage - 1)}>Prev</button>
                        <button className="Btn" onClick={() => setReqPage(reqPage + 1)}>Next</button>
                    </>
                ) : (
                    <>
                        <button className="Btn" disabled={updatePageIndex === 0} onClick={() => setUpdatePageIndex(updatePageIndex - 1)}>Prev</button>
                        <button className="Btn" disabled={!nextSeek} onClick={() => setUpdatePageIndex(updatePageIndex + 1)}>Next</button>
                    </>
                )}
            </div>
            {type === "request" && <button className="Btn Danger" onClick={() => deleteRequestLogs().then(loadLogs)}>Delete All</button>}
        </div>
    );
}

// ===== REUSABLE TABLE COMPONENT =====
function Table({ data = [], actions = [], onAction = () => {} }) {
    if (!data || data.length === 0) {
        return <p style={{ fontSize: "12px", fontStyle: "italic", color: "#666" }}>No records found.</p>;
    }

    const headers = Object.keys(data[0]);

    return (
        <div className="TableCard">
            <div className="TableScroll">
                <table>
                    <thead>
                        <tr>
                            {headers.map(h => (
                                <th key={h}>{h.replace(/_/g, " ").toUpperCase()}</th>
                            ))}
                            {actions.length > 0 && <th>ACTIONS</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, idx) => (
                            <tr key={row.uuidstring || row.id || idx}>
                                {headers.map(h => (
                                    <td key={h}>
                                        {typeof row[h] === "object" && row[h] !== null
                                            ? JSON.stringify(row[h])
                                            : String(row[h] ?? "")}
                                    </td>
                                ))}
                                {actions.length > 0 && (
                                    <td>
                                        {actions.map(act => (
                                            <button
                                                key={act}
                                                className={`Btn ${act === "Delete" ? "Danger" : act === "Select" ? "Primary" : ""}`}
                                                onClick={() => onAction(act, row)}
                                            >
                                                {act}
                                            </button>
                                        ))}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}